import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Save, Undo2, Plus, Trash2, FileText, Info, Eye, EyeOff, Download, RotateCcw } from "lucide-react";

/**
 * StatementFormatEditor
 * ──────────────────────
 * Admin / Owner / CFO / Accountant edits per-org financial statement
 * customisations that back /api/financial-statements/config:
 *   - bs_renames        : { bucket_key: new_display_label }
 *   - bs_hidden_buckets : [bucket_key]
 *   - pl_renames        : { bucket_key: new_display_label }
 *   - pl_hidden_buckets : [bucket_key]
 *   - cf_renames        : { section_key: new_display_label }   e.g. { operating: "..." }
 *   - cf_hidden_buckets : [section_key]                        e.g. ["investing"]
 *   - custom_notes      : [{title, body, reference}]
 *
 * The bucket keys come from Schedule III / financial_statements_engine so we
 * show them with a helpful default label pulled live from /balance-sheet
 * and /pnl (use_customisation=false).
 */

/** Convert snake_case_key to Title Case for the default label. */
const humanise = (s) =>
  s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/** Walk the BS response and yield leaf-bucket keys we can customise. */
const bsKeysFromResponse = (bs) => {
  const out = [];
  const walk = (obj, path = "") => {
    if (!obj || typeof obj !== "object") return;
    Object.keys(obj).forEach((k) => {
      if (["total", "grand_total"].includes(k)) return;
      if (typeof obj[k] === "object" && !Array.isArray(obj[k])) {
        walk(obj[k], path + k + ".");
      } else {
        out.push({ key: k, group: path.split(".")[0] || "root" });
      }
    });
  };
  walk(bs?.equity_and_liabilities);
  walk(bs?.assets);
  return out;
};

const plKeysFromResponse = (pl) => {
  const out = [];
  ["revenue", "expenses"].forEach((sec) => {
    Object.keys(pl?.[sec] || {}).forEach((k) => {
      if (!["total_revenue", "total_expenses", "total"].includes(k)) {
        out.push({ key: k, group: sec });
      }
    });
  });
  return out;
};

export default function StatementFormatEditor() {
  const [cfg, setCfg] = useState(null);
  const [savedCfg, setSavedCfg] = useState(null);   // last-persisted, for before/after preview
  const [bsKeys, setBsKeys] = useState([]);
  const [plKeys, setPlKeys] = useState([]);
  const [cfDefaults, setCfDefaults] = useState({});
  const [family, setFamily] = useState("company");
  const [busy, setBusy] = useState(false);
  const [newNote, setNewNote] = useState({ title: "", body: "", reference: "" });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewBefore, setPreviewBefore] = useState(null);
  const [previewAfter, setPreviewAfter] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewSection, setPreviewSection] = useState("bs");   // bs | pl | cf

  const load = async () => {
    setBusy(true);
    try {
      const [c, fam, bsRaw, plRaw, caps] = await Promise.all([
        api.get("/financial-statements/config"),
        api.get("/financial-statements/family"),
        api.get("/financial-statements/balance-sheet", { params: { use_customisation: false } }),
        api.get("/financial-statements/pnl", { params: { use_customisation: false } }),
        api.get("/financial-statements/caption-defaults"),
      ]);
      setCfg({
        bs_renames: c.data.bs_renames || {},
        bs_hidden_buckets: c.data.bs_hidden_buckets || [],
        pl_renames: c.data.pl_renames || {},
        pl_hidden_buckets: c.data.pl_hidden_buckets || [],
        cf_renames: c.data.cf_renames || {},
        cf_hidden_buckets: c.data.cf_hidden_buckets || [],
        custom_notes: c.data.custom_notes || [],
      });
      setSavedCfg({
        bs_renames: c.data.bs_renames || {},
        bs_hidden_buckets: c.data.bs_hidden_buckets || [],
        pl_renames: c.data.pl_renames || {},
        pl_hidden_buckets: c.data.pl_hidden_buckets || [],
        cf_renames: c.data.cf_renames || {},
        cf_hidden_buckets: c.data.cf_hidden_buckets || [],
      });
      setFamily(fam.data.family || "company");
      setBsKeys(bsKeysFromResponse(bsRaw.data));
      setPlKeys(plKeysFromResponse(plRaw.data));
      setCfDefaults(caps.data?.cashflow || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load statement config");
    }
    setBusy(false);
  };

  useEffect(() => { load(); }, []);

  // Preview effect (declared BEFORE the early return to satisfy
  // react-hooks/rules-of-hooks — CRA treats hook violations as fatal in
  // production builds). runPreview is defined further below; the callback
  // captures it lexically and only fires after render completes, so its
  // temporal-dead-zone status during declaration is fine.
  useEffect(() => {
    if (previewOpen && cfg && savedCfg) runPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewOpen]);

  if (!cfg) return <div className="p-4 italic text-muted-foreground">Loading statement format editor…</div>;

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/financial-statements/config", cfg);
      toast.success("Statement format saved — reports will use it immediately");
      setSavedCfg({
        bs_renames: cfg.bs_renames, bs_hidden_buckets: cfg.bs_hidden_buckets,
        pl_renames: cfg.pl_renames, pl_hidden_buckets: cfg.pl_hidden_buckets,
        cf_renames: cfg.cf_renames, cf_hidden_buckets: cfg.cf_hidden_buckets,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setBusy(false);
  };

  const runPreview = async () => {
    setPreviewBusy(true);
    try {
      // 'Before': run with the currently persisted config
      // 'After' : run with the unsaved edits in cfg
      const [before, after] = await Promise.all([
        api.post("/financial-statements/preview", {
          custom: { ...savedCfg, custom_notes: [] },
        }),
        api.post("/financial-statements/preview", {
          custom: { ...cfg, custom_notes: [] },
        }),
      ]);
      setPreviewBefore(before.data);
      setPreviewAfter(after.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
    setPreviewBusy(false);
  };

  const reset = async () => {
    if (!window.confirm("Reset every customisation back to the default statutory format?")) return;
    setCfg({
      bs_renames: {}, bs_hidden_buckets: [],
      pl_renames: {}, pl_hidden_buckets: [],
      cf_renames: {}, cf_hidden_buckets: [],
      custom_notes: cfg.custom_notes,
    });
    toast.info("Reset locally — click Save to persist");
  };

  const cfSections = ["operating", "investing", "financing", "net_change"];
  const cfDefaultCaptions = cfDefaults[family] || cfDefaults.company || {};

  const toggleHidden = (bucket, list, setList) => {
    setCfg({ ...cfg, [list]: cfg[list].includes(bucket)
      ? cfg[list].filter(x => x !== bucket)
      : [...cfg[list], bucket] });
  };

  const renderRenameList = (keys, renameKey, hideKey) => (
    <div className="max-h-[420px] overflow-auto rounded-sm border">
      <table className="w-full text-xs">
        <thead className="bg-muted/40 text-[10px] font-mono uppercase text-muted-foreground sticky top-0">
          <tr>
            <th className="text-left p-2">Statutory key</th>
            <th className="text-left p-2">Group</th>
            <th className="text-left p-2">Your label (blank = default)</th>
            <th className="text-center p-2">Hide</th>
          </tr>
        </thead>
        <tbody>
          {keys.map(({ key, group }) => (
            <tr key={key} className="border-t" data-testid={`sfe-row-${key}`}>
              <td className="p-2 font-mono text-[11px]">{key}</td>
              <td className="p-2 text-muted-foreground text-[10px] uppercase">{group}</td>
              <td className="p-2">
                <Input
                  value={cfg[renameKey][key] ?? ""}
                  placeholder={humanise(key)}
                  onChange={(e) => setCfg({
                    ...cfg,
                    [renameKey]: { ...cfg[renameKey], [key]: e.target.value },
                  })}
                  onBlur={(e) => {
                    // Trim empty entries so we never save {"x": ""}
                    if (!e.target.value.trim()) {
                      const { [key]: _, ...rest } = cfg[renameKey];
                      setCfg({ ...cfg, [renameKey]: rest });
                    }
                  }}
                  className="rounded-sm h-8 text-xs"
                  data-testid={`sfe-rename-${key}`}
                />
              </td>
              <td className="p-2 text-center">
                <Checkbox
                  checked={cfg[hideKey].includes(key)}
                  onCheckedChange={() => toggleHidden(key, hideKey, hideKey)}
                  data-testid={`sfe-hide-${key}`}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="statement-format-editor">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">
            Statement Format Editor · {family}
          </div>
          <div className="text-sm text-muted-foreground mt-1 max-w-prose">
            Rename any statutory line-item, hide sections that don't apply,
            and add narrative notes to the Balance Sheet, P&amp;L or Cash-Flow
            statement. Your changes apply to every report/PDF instantly.
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setPreviewOpen(true)}
                    className="rounded-sm gap-1.5" data-testid="sfe-preview">
            <Eye className="w-3.5 h-3.5" /> Preview
          </Button>
          <Button variant="outline" size="sm"
                    onClick={async () => {
                      try {
                        const r = await api.get("/financial-statements/pack.pdf",
                                                    { responseType: "blob" });
                        const url = URL.createObjectURL(r.data);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `Financial_Statements_${new Date().toISOString().slice(0,10)}.pdf`;
                        a.click();
                        URL.revokeObjectURL(url);
                        toast.success("Financial Statement PDF downloaded");
                      } catch (e) {
                        toast.error("PDF download failed");
                      }
                    }}
                    className="rounded-sm gap-1.5 border-blue-500 text-blue-700 hover:bg-blue-50"
                    data-testid="sfe-pdf-pack">
            <Download className="w-3.5 h-3.5" /> Download PDF pack
          </Button>
          <Button variant="outline" size="sm" onClick={reset} className="rounded-sm gap-1.5"
                    data-testid="sfe-reset">
            <Undo2 className="w-3.5 h-3.5" /> Reset all
          </Button>
          <Button size="sm" onClick={save} disabled={busy}
                    className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5"
                    data-testid="sfe-save">
            <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="bs">
        <TabsList className="rounded-sm">
          <TabsTrigger value="bs" className="rounded-sm" data-testid="sfe-tab-bs">Balance Sheet</TabsTrigger>
          <TabsTrigger value="pl" className="rounded-sm" data-testid="sfe-tab-pl">Profit &amp; Loss</TabsTrigger>
          <TabsTrigger value="cf" className="rounded-sm" data-testid="sfe-tab-cf">Cash Flow</TabsTrigger>
          <TabsTrigger value="notes" className="rounded-sm" data-testid="sfe-tab-notes">Notes to Accounts</TabsTrigger>
        </TabsList>

        <TabsContent value="bs" className="mt-3">
          <Card className="p-3 rounded-sm">
            {renderRenameList(bsKeys, "bs_renames", "bs_hidden_buckets")}
          </Card>
        </TabsContent>

        <TabsContent value="pl" className="mt-3">
          <Card className="p-3 rounded-sm">
            {renderRenameList(plKeys, "pl_renames", "pl_hidden_buckets")}
          </Card>
        </TabsContent>

        <TabsContent value="cf" className="mt-3">
          <Card className="p-3 rounded-sm">
            <div className="text-xs text-muted-foreground mb-3 flex items-start gap-2">
              <Info className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                Default captions for <b>{family}</b>. Blank = keep default.
                Hide an entire section (Operating / Investing / Financing) to
                exclude it from the Cash-Flow report.
              </span>
            </div>
            <table className="w-full text-xs">
              <thead className="bg-muted/40 text-[10px] font-mono uppercase text-muted-foreground">
                <tr>
                  <th className="text-left p-2">Section</th>
                  <th className="text-left p-2">Default caption</th>
                  <th className="text-left p-2">Your caption</th>
                  <th className="text-center p-2">Hide</th>
                </tr>
              </thead>
              <tbody>
                {cfSections.map((sec) => (
                  <tr key={sec} className="border-t" data-testid={`sfe-cf-row-${sec}`}>
                    <td className="p-2 font-mono text-[11px] uppercase">{sec}</td>
                    <td className="p-2 text-muted-foreground text-xs">
                      {cfDefaultCaptions[sec] || "—"}
                    </td>
                    <td className="p-2">
                      <Input
                        value={cfg.cf_renames[sec] ?? ""}
                        placeholder={cfDefaultCaptions[sec] || ""}
                        onChange={(e) => setCfg({
                          ...cfg,
                          cf_renames: { ...cfg.cf_renames, [sec]: e.target.value },
                        })}
                        onBlur={(e) => {
                          if (!e.target.value.trim()) {
                            const { [sec]: _, ...rest } = cfg.cf_renames;
                            setCfg({ ...cfg, cf_renames: rest });
                          }
                        }}
                        className="rounded-sm h-8 text-xs"
                        data-testid={`sfe-cf-rename-${sec}`}
                      />
                    </td>
                    <td className="p-2 text-center">
                      {["operating", "investing", "financing"].includes(sec) && (
                        <Checkbox
                          checked={cfg.cf_hidden_buckets.includes(sec)}
                          onCheckedChange={() => toggleHidden(sec, "cf_hidden_buckets", "cf_hidden_buckets")}
                          data-testid={`sfe-cf-hide-${sec}`}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="notes" className="mt-3">
          <Card className="p-3 rounded-sm space-y-3">
            <div className="text-xs text-muted-foreground flex items-start gap-2">
              <Info className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                Custom notes appended to <b>Notes to Accounts</b> in
                every exported PDF. Common uses: Note-1 significant
                accounting policies, contingent liabilities, related-party
                disclosures, going-concern statement.
              </span>
            </div>
            <div className="space-y-2">
              {(cfg.custom_notes || []).map((n, i) => (
                <div key={i} className="border rounded-sm p-3 text-sm" data-testid={`sfe-note-${i}`}>
                  <div className="flex items-start justify-between gap-2">
                    <b className="text-xs">{n.title}</b>
                    <Button size="icon" variant="ghost"
                             onClick={() => setCfg({
                               ...cfg,
                               custom_notes: cfg.custom_notes.filter((_, x) => x !== i),
                             })}
                             className="h-6 w-6" data-testid={`sfe-note-del-${i}`}>
                      <Trash2 className="w-3 h-3 text-red-500" />
                    </Button>
                  </div>
                  {n.reference && <div className="text-[10px] font-mono uppercase text-accent mt-1">Ref: {n.reference}</div>}
                  <div className="text-xs text-muted-foreground whitespace-pre-wrap mt-1">{n.body}</div>
                </div>
              ))}
              {(cfg.custom_notes || []).length === 0 && (
                <div className="italic text-xs text-muted-foreground text-center py-4">
                  No custom notes yet. Add one below.
                </div>
              )}
            </div>
            <div className="border rounded-sm p-3 space-y-2">
              <div className="text-[10px] font-mono uppercase text-accent">New note</div>
              <Input placeholder="Title (e.g. 1. Significant accounting policies)"
                       value={newNote.title}
                       onChange={(e) => setNewNote({ ...newNote, title: e.target.value })}
                       className="rounded-sm h-8 text-xs" data-testid="sfe-note-title" />
              <Input placeholder="Reference (e.g. AS-1 / Ind AS-1)"
                       value={newNote.reference}
                       onChange={(e) => setNewNote({ ...newNote, reference: e.target.value })}
                       className="rounded-sm h-8 text-xs" data-testid="sfe-note-ref" />
              <Textarea placeholder="Note body…" rows={3}
                          value={newNote.body}
                          onChange={(e) => setNewNote({ ...newNote, body: e.target.value })}
                          className="rounded-sm text-xs" data-testid="sfe-note-body" />
              <Button size="sm" onClick={() => {
                if (!newNote.title.trim()) return toast.error("Note needs a title");
                setCfg({
                  ...cfg,
                  custom_notes: [...(cfg.custom_notes || []), { ...newNote }],
                });
                setNewNote({ title: "", body: "", reference: "" });
                toast.success("Note added (click Save to persist)");
              }} className="rounded-sm gap-1.5" data-testid="sfe-note-add">
                <Plus className="w-3 h-3" /> Add note
              </Button>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Preview dialog — side-by-side before/after */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="rounded-sm sm:max-w-6xl max-h-[92vh] overflow-hidden flex flex-col"
                        data-testid="sfe-preview-dialog">
          <DialogHeader>
            <DialogTitle>Statement Format · live preview</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-muted-foreground">
            Left column = currently saved. Right column = unsaved edits. Line
            items that changed are highlighted.
          </div>
          <div className="flex gap-2 mb-2 mt-1">
            {[
              ["bs", "Balance Sheet"],
              ["pl", "Profit & Loss"],
              ["cf", "Cash Flow"],
            ].map(([k, l]) => (
              <Button key={k} size="sm"
                        variant={previewSection === k ? "default" : "outline"}
                        onClick={() => setPreviewSection(k)}
                        className={`rounded-sm h-8 text-xs ${previewSection === k ? "bg-accent text-white" : ""}`}
                        data-testid={`sfe-preview-tab-${k}`}>
                {l}
              </Button>
            ))}
            <div className="flex-1" />
            <Button size="sm" variant="outline" onClick={runPreview} disabled={previewBusy}
                      className="rounded-sm h-8 gap-1.5" data-testid="sfe-preview-refresh">
              <RotateCcw className="w-3 h-3" /> {previewBusy ? "Loading…" : "Refresh"}
            </Button>
          </div>
          <div className="flex-1 overflow-auto grid grid-cols-2 gap-3 pr-1">
            <StatementPreviewColumn
              title="Before (saved)"
              badge="Saved"
              tone="muted"
              section={previewSection}
              data={previewBefore}
              otherData={previewAfter}
              busy={previewBusy}
            />
            <StatementPreviewColumn
              title="After (unsaved)"
              badge="Preview"
              tone="accent"
              section={previewSection}
              data={previewAfter}
              otherData={previewBefore}
              busy={previewBusy}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/** Renders one column of the before/after preview. `data` is a preview
 *  payload from /api/financial-statements/preview; `otherData` is used only
 *  for the diff-highlight so we know whether a line item's label changed. */
function StatementPreviewColumn({ title, badge, tone, section, data, otherData, busy }) {
  const badgeClass = tone === "accent"
    ? "bg-accent/10 text-accent border-accent/30"
    : "bg-muted text-muted-foreground border-border";
  if (busy || !data) {
    return (
      <div className="border rounded-sm p-3 text-xs italic text-muted-foreground">
        Loading preview…
      </div>
    );
  }

  const fmt = (v) => {
    if (v === null || v === undefined) return "—";
    if (typeof v !== "number") return String(v);
    return v.toLocaleString("en-IN", { maximumFractionDigits: 2 });
  };
  const humanise = (s) => s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  // Build a flat set of (label, value, key) rows, comparing against otherData
  const flatBs = (bs) => {
    const rows = [];
    const walk = (obj, indent = 0, prefix = "") => {
      Object.entries(obj || {}).forEach(([k, v]) => {
        if (["total", "grand_total"].includes(k)) return;
        if (v && typeof v === "object" && !Array.isArray(v)) {
          rows.push({ key: prefix + k, label: humanise(k), value: v.total ?? null, indent, isGroup: true });
          walk(v, indent + 1, prefix + k + ".");
        } else if (!Array.isArray(v)) {
          rows.push({ key: prefix + k, label: humanise(k), value: v, indent });
        }
      });
    };
    walk(bs?.equity_and_liabilities || {});
    rows.push({ key: "__separator", label: "— Assets —", separator: true });
    walk(bs?.assets || {});
    return rows;
  };
  const flatPl = (pl) => {
    const rows = [];
    ["revenue", "expenses"].forEach((sec) => {
      rows.push({ key: `__${sec}`, label: humanise(sec), header: true });
      Object.entries(pl?.[sec] || {}).forEach(([k, v]) => {
        if (["total_revenue", "total_expenses", "total"].includes(k)) return;
        rows.push({ key: sec + "." + k, label: humanise(k), value: v, indent: 1 });
      });
    });
    return rows;
  };
  const flatCf = (cf) => {
    const rows = [];
    const captions = cf.captions || {};
    ["operating", "investing", "financing"].forEach((sec) => {
      rows.push({ key: "__" + sec, label: captions[sec] || humanise(sec), header: true });
      (cf.categories || []).filter(c => c.section === sec).forEach((c) => {
        rows.push({ key: sec + "." + c.name, label: c.name, value: c.amount, indent: 1 });
      });
      rows.push({ key: sec + ".subtotal", label: `Sub-total ${sec}`, value: cf[sec], indent: 1, isGroup: true });
    });
    rows.push({ key: "net_change", label: captions.net_change || "Net Change", value: cf.net_change, isGroup: true });
    return rows;
  };

  let rows = [];
  if (section === "bs") rows = flatBs(data.balance_sheet);
  else if (section === "pl") rows = flatPl(data.pnl);
  else if (section === "cf") rows = flatCf(data.cash_flow);

  // For diff-highlight: build a map of the other column's labels + values
  let otherRows = [];
  if (otherData) {
    if (section === "bs") otherRows = flatBs(otherData.balance_sheet);
    else if (section === "pl") otherRows = flatPl(otherData.pnl);
    else if (section === "cf") otherRows = flatCf(otherData.cash_flow);
  }
  const otherByKey = {};
  otherRows.forEach(r => { otherByKey[r.key] = r; });

  return (
    <div className="border rounded-sm overflow-hidden">
      <div className="flex items-center justify-between p-2 border-b bg-muted/30">
        <div className="font-semibold text-xs">{title}</div>
        <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-sm border ${badgeClass}`}>{badge}</span>
      </div>
      <table className="w-full text-[11px]">
        <tbody>
          {rows.map((r) => {
            if (r.separator) {
              return (
                <tr key={r.key} className="bg-muted/50">
                  <td colSpan={2} className="p-1.5 font-mono uppercase text-[9px] text-center text-muted-foreground">
                    {r.label}
                  </td>
                </tr>
              );
            }
            if (r.header) {
              return (
                <tr key={r.key} className="bg-accent/5">
                  <td colSpan={2} className="p-1.5 font-semibold text-xs">{r.label}</td>
                </tr>
              );
            }
            const other = otherByKey[r.key];
            const labelChanged = other && other.label !== r.label;
            const valueChanged = other && other.value !== r.value;
            const missing      = !other;
            const rowClass = missing
              ? "bg-red-50 text-red-700"
              : (labelChanged || valueChanged) ? "bg-amber-50" : "";
            return (
              <tr key={r.key} className={`border-t ${rowClass}`}>
                <td className="p-1.5" style={{ paddingLeft: 6 + (r.indent || 0) * 12 }}>
                  <span className={r.isGroup ? "font-semibold" : ""}>{r.label}</span>
                  {labelChanged && <span className="ml-1 text-[9px] font-mono uppercase text-amber-700">renamed</span>}
                  {missing && <span className="ml-1 text-[9px] font-mono uppercase">hidden</span>}
                </td>
                <td className="p-1.5 text-right tabular-nums">
                  {r.value === null || r.value === undefined ? "" : fmt(r.value)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
